import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/button';
import { useDispatch } from 'state/contextState';
import { Duration } from 'luxon';
import { ADJUST_TIME } from 'reducers/homePageReducer';
import styled, { css } from 'styled-components';
import DownSvg from 'svg/arrowDown/styledArrowDown'; // Changed to import default
import UpSvg from 'svg/arrowUp/styledArrowUp'; // Changed to import default

const Time = ({
  timeSeconds = 365,
  isEditable,
  refKey = 0,
  type,
}) => {
  const dispatch = useDispatch();
  const duration = Duration.fromObject({
    seconds: timeSeconds,
  });

  const timeFragments = duration.toFormat('hh : mm : ss').split(':');
  const decimal = duration.as('hours').toFixed(2);

  const memoAddHour = useCallback(() => {
    dispatch({ type: ADJUST_TIME, seconds: 3600, key: refKey });
  }, [dispatch, refKey]);

  const memoSubtractHour = useCallback(() => {
    dispatch({ type: ADJUST_TIME, seconds: -3600, key: refKey });
  }, [dispatch, refKey]);

  const memoAddMinute = useCallback(() => {
    dispatch({ type: ADJUST_TIME, seconds: 60, key: refKey });
  }, [dispatch, refKey]);

  const memoSubtractMinute = useCallback(() => {
    dispatch({ type: ADJUST_TIME, seconds: -60, key: refKey });
  }, [dispatch, refKey]);

  return (
    <TimeWrapper type={type}>
      {isEditable ? (
        <EditableTime>
          <ArrowButton
            type="up"
            onClick={memoAddHour}
          />
          <TimeCell>{timeFragments[0]}</TimeCell>
          <ArrowButton
            type="down"
            onClick={memoSubtractHour}
          />
        </EditableTime>
      ) : (
        <TimeCell>{timeFragments[0]}</TimeCell>
      )}
      <SeparatorCell>:</SeparatorCell>
      {isEditable ? (
        <EditableTime>
          <ArrowButton
            type="up"
            onClick={memoAddMinute}
          />
          <TimeCell>{timeFragments[1]}</TimeCell>
          <ArrowButton
            type="down"
            onClick={memoSubtractMinute}
          />
        </EditableTime>
      )
        : (
          <TimeCell>{timeFragments[1]}</TimeCell>
        )}
      <SeparatorCell>:</SeparatorCell>
      <TimeCell>{timeFragments[2]}</TimeCell>
      <SeparatorCell>|</SeparatorCell>
      <TimeCell>{decimal}</TimeCell>
    </TimeWrapper>
  );
};

Time.propTypes = {
  timeSeconds: PropTypes.number,
  isEditable: PropTypes.bool,
  refKey: PropTypes.number,
  type: PropTypes.string,
};

const TimeWrapper = memo(styled.div`
  font-family: Roboto;
  font-weight: 300;
  font-style: normal;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  ${(props) => props.type === 'large' && css`
    height: 36px;
    font-size: 36px;
    color: ${props.theme.main.largeTimer};
`};
  ${(props) => props.type === 'small' && css`
    font-size: 18px;
    height: 18px;
    color: ${props.theme.main.smallTimer};
    &:hover {
      ${DownSvg},${UpSvg}{
          opacity: 0.3;
         }
     }
`};
`);

const ArrowButton = memo(styled(Button)`
  &:hover{
    ${DownSvg}, ${UpSvg}{
       opacity: 1;
       }
  }
  &:active{
    ${DownSvg}, ${UpSvg}{
       opacity: 0.3;
       }
  }
`);

const EditableTime = memo(styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
`);

const TimeCell = memo(styled.div`
  text-align: center;
`);

const SeparatorCell = memo(styled(TimeCell)`
  margin: 0 10px 0 10px;
`);

// Time.whyDidYouRender = true;

export default memo(Time);
